import { useState, useCallback, ReactNode } from "react";
import { OrderHistoryFilterContext } from "../context/OrderHistoryFilterContext";
import OrderHistoryFilterContextType from "../interfaces/context/OrderHistoryContextType";
import OrderHistoryFilterState from "../interfaces/OrderHistoryFilter";
import { OrderHistoryFilterFieldEnum } from "@/enums/orderHistoryFilterFiledEnum";
import { SortByOptionsValueEnum } from "@/enums/sortByOptionsValueEnum";
import { StatusGroupEnum } from "@/enums/StatusGroupEnum";

export const initialFilterState: OrderHistoryFilterState = {
  [OrderHistoryFilterFieldEnum.ORDER_NUMBER]: "",
  [OrderHistoryFilterFieldEnum.JOB_NAME]: "",
  [OrderHistoryFilterFieldEnum.ITEM_NUMBER]: "",
  [OrderHistoryFilterFieldEnum.ITEM_DESCRIPTION]: "",
  [OrderHistoryFilterFieldEnum.ORDER_FILTER]: "",
  [OrderHistoryFilterFieldEnum.START_DATE]: null,
  [OrderHistoryFilterFieldEnum.END_DATE]: null,
  [OrderHistoryFilterFieldEnum.INVOICE_START_DATE]: null,
  [OrderHistoryFilterFieldEnum.INVOICE_END_DATE]: null,
  [OrderHistoryFilterFieldEnum.ORDER_STATUS_IDS]: [],
  [OrderHistoryFilterFieldEnum.MANUFACTURERS]: [],
  [OrderHistoryFilterFieldEnum.ORDER_SUB_STATUS_IDS]: [],
  [OrderHistoryFilterFieldEnum.ORDER_SHIP_TOS]: [],
  [OrderHistoryFilterFieldEnum.CATEGORY_IDS]: [],
  [OrderHistoryFilterFieldEnum.PICKUP_IN_STORE]: null,
  [OrderHistoryFilterFieldEnum.ADVANCED_SEARCH_EXPANDED]: false,
  [OrderHistoryFilterFieldEnum.TYPE_IDS]: [],
  [OrderHistoryFilterFieldEnum.SORT_BY]: SortByOptionsValueEnum.ORDER_DATE_DESC,
  [OrderHistoryFilterFieldEnum.RETRIGGER_KEY]: 0,
};

export const OrderHistoryFilterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [activeGroup, setActiveGroup] = useState<StatusGroupEnum>(
    StatusGroupEnum.ALL
  );

  const [filters, setFilters] =
    useState<OrderHistoryFilterState>(initialFilterState);

  const updateFilter = useCallback(
    <T extends keyof OrderHistoryFilterState>(
      field: T,
      value: OrderHistoryFilterState[T]
    ) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [field]: value,
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters(initialFilterState);
  }, []);

  const updateMultipleFilters = useCallback(
    (newFilters: Partial<OrderHistoryFilterState>) => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...newFilters,
      }));
    },
    []
  );

  const incrementRetriggerKey = useCallback(() => {
    setFilters((prevFilters) => {
      const currentKey =
        prevFilters[OrderHistoryFilterFieldEnum.RETRIGGER_KEY] || 0;

      return {
        ...prevFilters,
        [OrderHistoryFilterFieldEnum.RETRIGGER_KEY]: currentKey + 1,
      };
    });
  }, []);

  const contextValue: OrderHistoryFilterContextType = {
    filters,
    updateFilter,
    resetFilters,
    updateMultipleFilters,
    incrementRetriggerKey,
    activeGroup,
    setActiveGroup,
  };

  return (
    <OrderHistoryFilterContext.Provider value={contextValue}>
      {children}
    </OrderHistoryFilterContext.Provider>
  );
};
